import { themes, useNavigationContext } from '@infotech-soe/erp-ui-kit';
import React, { memo, MouseEvent, useCallback, useState } from 'react';
import { useIntl } from 'react-intl';

import { AllowedThemeName } from '../../../../constants/theme';

import { messages } from './messages';
import { useItemStyles } from './styles';

interface ThemeSelectorItemProps {
  name: AllowedThemeName;
}

const ThemeSelectorItem = ({
  name,
}: ThemeSelectorItemProps): React.JSX.Element => {
  const { formatMessage } = useIntl();
  const classes = useItemStyles();

  const { active, onActivate, onRegister } = useNavigationContext();
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      onActivate(event, name);
      event.currentTarget.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    },
    [name, onActivate]
  );

  const isSelected = active === name;
  const themeColors = themes[name].colors;

  const colorsForExample = [
    'backgroundMenu',
    'backgroundHeader',
    'primary',
    'secondary',
  ] as const;
  const colorExamples = colorsForExample.map(color => themeColors[color]);

  const borderColor =
    isSelected && isHovered
      ? themeColors.primaryHover
      : isSelected || isHovered
        ? themeColors.primary
        : 'transparent';

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      className={classes.themeCard}
      style={{ borderColor }}
      ref={node => onRegister(node, name)}
      tabIndex={active === name ? 0 : -1}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className={classes.bigColorPatch}>
        {colorExamples.map((example, idx) => (
          <div
            key={name + colorsForExample[idx]}
            style={{ backgroundColor: example }}
          />
        ))}
      </div>
      <span>{formatMessage(messages[name])}</span>
    </div>
  );
};

export default memo(ThemeSelectorItem);
